import { call, takeLatest, select, put } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { makeBackendApi, ITravelAgentResponse, ITravelAgentViaUuidResponse, ICompanyResponse } from 'services/BackendApi';
import {
  GetTravelAgentRequestAction,
  loadSelectedTaViaUuidFailureAction,
  LoadSelectedTaViaUuidRequestAction,
  GetMembershipsRequestAction,
  getMembershipsSuccessAction,
  getMembershipsFailureAction,
  SetSelectedMembershipBasedOnTaRequestAction,
  setSelectedMembershipBasedOnTaFailureAction,
  SET_SELECTED_MEMBERSHIP_BASED_ON_TA_REQUEST,
  setSelectedCompanyMembershipAction,
  setSelectedMembershipBasedOnTaSuccessAction,
  LOAD_SELECTED_COMPANY_VIA_UUID_REQUEST,
  LoadSelectedCompanyViaUuidRequestAction,
  selectedCompanyChangeAction,
  loadSelectedCompanyViaUuidSuccessAction,
  loadSelectedCompanyViaUuidFailureAction,
} from '../actions';
import {
  getTravelAgentsSuccessAction,
  getTravelAgentsFailureAction,
  GET_TRAVEL_AGENTS_REQUEST,
  LOAD_SELECTED_TA_VIA_UUID_REQUEST,
  loadSelectedTaViaUuidSuccessAction,
  GET_MEMBERSHIPS_REQUEST,
} from '../actions';
import { agentsSelector, selectedTaSelector } from '../selectors';
import * as AgentsActions from 'store/modules/agents/actions';

export function* getTravelAgentsRequestSaga(action: GetTravelAgentRequestAction) {
  try {
    const selectedTa = yield select(selectedTaSelector);
    const backendApi = makeBackendApi(selectedTa?.uuid);
    const currentAgents = yield select(agentsSelector);

    if (!currentAgents || (currentAgents && action.forceLoad)) {
      const result: AxiosResponse<ITravelAgentResponse> = yield call(backendApi.getTravelAgents);
      yield put(getTravelAgentsSuccessAction(result.data.data));
    }

    if (currentAgents && !action.forceLoad) {
      yield put(getTravelAgentsSuccessAction(currentAgents));
    }
  } catch (e) {
    yield put(getTravelAgentsFailureAction(e.message));
  }
}

export function* loadSelectedTaViaUuidSaga(action: LoadSelectedTaViaUuidRequestAction) {
  try {
    const backendApi = makeBackendApi(action.tauuid);

    const result: AxiosResponse<ITravelAgentResponse> = yield call(backendApi.getTravelAgentViaUuid, action.tauuid);
    yield put(loadSelectedTaViaUuidSuccessAction(result.data.data));
  } catch (e) {
    yield put(loadSelectedTaViaUuidFailureAction('Couldnt load travel agent'));
  }
}

export function* getMembershipsSaga(action: GetMembershipsRequestAction) {
  try {
    const backendApi = makeBackendApi();
    const result: AxiosResponse = yield call(backendApi.fetchCompanyMemberships, action.companyUuid, {
      filter: {
        companyMembership: {
          companyUuid: action.companyUuid,
          status: true,
        },
      },
    });

    yield put(getMembershipsSuccessAction(result.data.data, action.resetSelected));
    if (action.setInBookingBuilder) {
      // OWA-10162: Pass null instead of undefined when array is empty to maintain type contract
      const membershipToSet = result.data.data[0] || null;
      yield put(AgentsActions.setSelectedCompanyMembershipAction(membershipToSet));
    }
  } catch (e) {
    yield put(getMembershipsFailureAction());
  }
}

export function* setSelectedMembershipBasedOnTaSaga(action: SetSelectedMembershipBasedOnTaRequestAction) {
  try {
    const backendApi = makeBackendApi();
    const travelAgentResponse: AxiosResponse<ITravelAgentViaUuidResponse> = yield call(backendApi.getTravelAgentViaUuid, action.taUuid);
    const travelAgent = travelAgentResponse.data.data;

    if (!travelAgent.companyUuid) {
      throw new Error('Travel Agent has no related company');
    }

    const companyMembershipsResponse = yield call(backendApi.fetchCompanyMemberships, travelAgent.companyUuid, {
      filter: {
        companyMembership: {
          companyUuid: travelAgent.companyUuid,
          status: true,
        },
      },
    });
    const selectedMembership = companyMembershipsResponse.data.data.find(item => item.uuid === action.membershipUuid);
    yield put(setSelectedCompanyMembershipAction(selectedMembership));

    yield put(setSelectedMembershipBasedOnTaSuccessAction());
  } catch (e) {
    yield put(setSelectedMembershipBasedOnTaFailureAction());
  }
}

export function* setSelectedCompanyBasedOnUuidSaga(action: LoadSelectedCompanyViaUuidRequestAction) {
  try {
    const backendApi = makeBackendApi();
    const companyResponse: AxiosResponse<ICompanyResponse> = yield call(backendApi.fetchCompany, action.companyUuid);
    const company = companyResponse.data.data;

    yield put(selectedCompanyChangeAction(company));
    yield put(loadSelectedCompanyViaUuidSuccessAction());
  } catch (e) {
    yield put(loadSelectedCompanyViaUuidFailureAction(e));
  }
}

export function* watchGetTravelAgentsRequest() {
  yield takeLatest([GET_TRAVEL_AGENTS_REQUEST], getTravelAgentsRequestSaga);
  yield takeLatest([LOAD_SELECTED_TA_VIA_UUID_REQUEST], loadSelectedTaViaUuidSaga);
  yield takeLatest([LOAD_SELECTED_COMPANY_VIA_UUID_REQUEST], setSelectedCompanyBasedOnUuidSaga);
  yield takeLatest([GET_MEMBERSHIPS_REQUEST], getMembershipsSaga);
  yield takeLatest([SET_SELECTED_MEMBERSHIP_BASED_ON_TA_REQUEST], setSelectedMembershipBasedOnTaSaga);
}
